<template>
  <v-container fill-height>
    <div>
      <v-app-bar :elevation="2" :style="{ backgroundColor: colors.primary }">
        <v-app-bar-title :style="{ color: colors.secondary }">
          {{ $t('evaluation.title') }}
        </v-app-bar-title>

        <LanguageSwitcher />

        <div v-if="jury">
          <v-btn icon id="menu-activator">
            <v-avatar size="36px">
              <v-img alt="Jury" :src="getJuryImageUrl(jury?.image)"></v-img>
            </v-avatar>
          </v-btn>
          <v-menu activator="#menu-activator">
            <v-list>
              <v-list-item @click="goToProfile">
                <v-list-item-title>{{ $t('menu.myProfile') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="goToHome">
                <v-list-item-title>{{ $t('menu.home') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="goToEvaluate">
                <v-list-item-title>{{ $t('menu.evaluation') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="logoutUser">
                <v-list-item-title>{{ $t('menu.logout') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div v-else>
          <v-btn
            @click="goToHome"
            icon="mdi-home"
            color="white"
            density="default"
          ></v-btn>
        </div>
      </v-app-bar>
      <!-- Add this overlay loader -->
      <div>
        <div class="d-flex align-center justify-center mb-4">
          <h1 class="mr-4 mb-0">{{ $t('results.title') }}</h1>
          <v-btn icon @click="fetchEvaluationResults" color="primary" class="mr-2">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn @click="downloadExcel" color="success" prepend-icon="mdi-file-excel">
            {{ $t('results.downloadExcel') }}
          </v-btn>
        </div>

        <div v-if="isLoading" class="loader-overlay">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
        </div>
        <div v-else>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('results.criteria') }}</th>
                  <th class="text-left">{{ $t('results.explanation') }}</th>
                  <!-- <th>Max Score</th> -->
                  <th style="min-width: 100px; text-align:center;" v-for="candidate in candidatesList" :key="candidate.id">
                    {{ candidate.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(criteria, index) in criterias.slice(0, 3)"
                  :key="index"
                >
                  <td class="text-left">{{ getCriteriaText(criteria) }}</td>
                  <td
                    class="text-left"
                    v-html="getExplanationText(criteria).replace('\n', '<br>')"
                  ></td>
                  <!-- <td>{{ criteria.max_score }}</td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    {{
                      evaluations[criteria.id] &&
                      evaluations[criteria.id][candidate.id]
                        ? evaluations[criteria.id][candidate.id]
                        : 0
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="text-left" colspan="2"><strong>{{ $t('results.total') }}</strong></td>
                  <!-- <td><strong>{{ getTotalMaxScoreForRows(1, 3) }}</strong></td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    <strong>{{
                      getTotalScoreForCandidateByRows(candidate.id, 1, 3)
                    }}</strong>
                  </td>
                </tr>
                <tr
                  v-for="(criteria, index) in criterias.slice(3, 5)"
                  :key="index"
                >
                  <td class="text-left">{{ getCriteriaText(criteria) }}</td>
                  <td
                    class="text-left"
                    v-html="getExplanationText(criteria).replace('\n', '<br>')"
                  ></td>
                  <!-- <td>{{ criteria.max_score }}</td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    {{
                      evaluations[criteria.id] &&
                      evaluations[criteria.id][candidate.id]
                        ? evaluations[criteria.id][candidate.id]
                        : 0
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="text-left" colspan="2"><strong>{{ $t('results.total') }}</strong></td>
                  <!-- <td><strong>{{ getTotalMaxScoreForRows(4, 5) }}</strong></td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    <strong>{{
                      getTotalScoreForCandidateByRows(candidate.id, 4, 5)
                    }}</strong>
                  </td>
                </tr>
                <tr v-for="(criteria, index) in criterias.slice(5)" :key="index">
                  <td class="text-left">{{ getCriteriaText(criteria) }}</td>
                  <td
                    class="text-left"
                    v-html="getExplanationText(criteria).replace('\n', '<br>')"
                  ></td>
                  <!-- <td>{{ criteria.max_score }}</td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    {{
                      evaluations[criteria.id] &&
                      evaluations[criteria.id][candidate.id]
                        ? evaluations[criteria.id][candidate.id]
                        : 0
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="text-left" colspan="2">
                    <strong>{{ $t('results.grandTotal') }}</strong>
                  </td>
                  <!-- <td><strong>{{ totalMaxScore }}</strong></td> -->
                  <td v-for="candidate in candidatesList" :key="candidate.id">
                    <strong>{{ getTotalScoreForCandidate(candidate.id) }}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="winners?.length > 0" class="mt-3 center-data">
            <v-row justify="center">
              <v-col
                v-for="winner in winners"
                :key="winner.name"
                cols="12"
                sm="12"
                md="3"
              >
                <!-- Winners Section -->
                <v-card
                  v-if="winner.total_score!=0"
                  :key="winner.name"
                  class="pa-4 winner-card mb-4"
                  :style="{
                    margin: 'auto',
                    border: '1px solid #e5e5e5',
                    borderRadius: '10px',
                  }"
                  elevation="2"
                >
                  <v-card-title
                    class="text-center"
                    style="font-size: 20px; font-weight: bold"
                  >
                    <v-icon color="yellow darken-3" class="mr-2"
                      >mdi-trophy</v-icon
                    >
                    {{ $t('results.winner') }}
                  </v-card-title>

                  <v-divider></v-divider>

                  <v-row align="center" justify="center" class="winner-row">
                    <v-col class="mt-5">
                      <v-img
                        :src="getCandidateImageUrl(winner.image)"
                        alt="Candidate"
                        min-width="200"
                      ></v-img>
                    </v-col>
                    <v-col>
                      <div style="font-weight: 600">{{ winner.name }}</div>
                      <div style="font-size: 14px; color: #777">
                        {{ $t('results.score') }}: {{ winner.total_score }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <!-- Other Candidates Section -->
            <div v-if="otherCandidates?.length > 0" class="other-winners mt-6">
              <h2 class="text-center mb-4">{{ $t('results.otherRankings') }}</h2>
              <v-row justify="center">
                <v-col
                  v-for="candidate in otherCandidates"
                  :key="candidate.name"
                  cols="12"
                  sm="4"
                  md="3"
                >
                  <v-card class="pa-4">
                    <v-card-title class="text-center">
                      <!-- {{ getRankingText(candidate.rank) }} -->
                    </v-card-title>
                    <v-img
                      :src="getCandidateImageUrl(candidate.image)"
                      :alt="candidate.name"
                      class="mb-3"
                      max-height="100"
                    ></v-img>
                    <v-card-text class="text-center">
                      <div style="font-weight: 600">{{ candidate.name }}</div>
                      <div style="font-size: 14px; color: #777">
                        {{ $t('results.score') }}: {{ candidate.total_score }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </div>
      <v-footer border app class="d-flex justify-center align-center p-4">
        {{ $t('common.developedBy') }} :
        <a
          href="https://thewebsquare.com"
          target="_blank"
          :style="{ color: colors.primary }"
          >The Web Square</a
        >
      </v-footer>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import colors from "../styles/colors";
import axios from "axios";
import api from "../api";
import LanguageSwitcher from './LanguageSwitcher.vue';
import * as XLSX from 'xlsx';

export default {
  name: "ResultsScreen",
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      results: [],
      winners: [],
      otherCandidates: [],
      colors,
      isLoading: false,
      criterias: [], // Array to store criteria data
      evaluations: {}, // Object to store evaluations by criteria and candidate_id
      candidatesList: [], // List to store candidates from the API
    };
  },
  computed: {
    ...mapState([
      "jury",
      "candidates",
      "selectedCandidate",
      "criteriasByCategory",
      "locale",
    ]),
    isEnglish() {
      return this.locale === 'en';
    },
    totalMaxScore() {
      return this.criterias.reduce(
        (sum, criteria) => sum + criteria.max_score,
        0
      );
    },
    filteredWinners() {
      return this.winners.filter((winner) => winner.total_score > 0);
    },
  },
  methods: {
    ...mapActions([
      "updateJury",
      "updateCandidates",
      "updateCriteriasByCategory",
      "logout",
      "updateSelectedCandidate",
    ]),

    async logoutUser() {
      try {
        await axios.post(api.juryLogout);
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.logout();
        this.$nextTick(() => {
          this.$router.push({ name: "JuryLogin" });
        });
      }
    },

    goToProfile() {
      this.$router.push({ name: 'JuryProfile' });
    },
    
    getJuryImageUrl(filename) {
      return api.getJuryImageUrl(filename); // Generate the full image URL
    },

    getCandidateImageUrl(filename) {
      return api.getCandidateImageUrl(filename); // Generate the full image URL
    },

    getRankingText(rank) {
      return rank ? `Classement: ${rank}` : "Classement inconnu";
    },

    async fetchEvaluationResults() {
      this.isLoading = true;
      try {
        // Check if user is authenticated (jury logged in)
        const isJuryAuthenticated = this.$store.getters.isAuthenticated;
        
        if (isJuryAuthenticated && this.jury) {
          // Jury is logged in - show only their country's results
          const response = await axios.get(api.juryMyCountryResults);
          
          if (response.data.success) {
            this.criterias = response.data.criterias;
            this.evaluations = response.data.evaluations;
            this.candidatesList = response.data.candidates;
          }

          // Fetch winners for jury's country
          const winnersResponse = await axios.get(api.juryMyCountryWinner);
          
          if (winnersResponse.data.success) {
            this.winners = winnersResponse.data.winners;
            this.otherCandidates = winnersResponse.data.other_candidates;
          }
        } else {
          // Public access - show all results
          const response = await axios.get(api.getGeneralResults);
          this.criterias = response.data.criterias;
          this.evaluations = response.data.evaluations;

          const candidatesResponse = await axios.get(api.getCandidates);
          this.candidatesList = candidatesResponse.data;

          const allWinners = await axios.get(api.getWinner);
          this.winners = allWinners.data.winners;
          this.otherCandidates = allWinners.data.other_candidates;
        }
      } catch (error) {
        console.error("Error fetching evaluation results or candidates:", error);
      } finally {
        this.isLoading = false;
      }
    },

    getTotalScoreForCandidate(candidateId) {
      return this.criterias.reduce((sum, criteria) => {
        const score =
          this.evaluations[criteria.id] &&
          this.evaluations[criteria.id][candidateId]
            ? this.evaluations[criteria.id][candidateId]
            : 0;
        return sum + +score;
      }, 0);
    },

    getTotalScoreForCandidateByRows(candidateId, startRow, endRow) {
      return this.criterias
        .slice(startRow - 1, endRow)
        .reduce((sum, criteria) => {
          const score =
            this.evaluations[criteria.id] &&
            this.evaluations[criteria.id][candidateId]
              ? this.evaluations[criteria.id][candidateId]
              : 0;
          return sum + +score;
        }, 0);
    },

    getTotalMaxScoreForRows(startRow, endRow) {
      return this.criterias
        .slice(startRow - 1, endRow)
        .reduce((sum, criteria) => sum + +criteria.max_score, 0);
    },

    goToEvaluate() {
      this.$router.push({ name: "EvaluationScreen" });
    },

    goToHome() {
      this.$router.push({ name: "WelcomeScreen" });
    },

    getCriteriaText(criteria) {
      return this.isEnglish && criteria.criteria_en ? criteria.criteria_en : criteria.criteria;
    },

    getExplanationText(criteria) {
      return this.isEnglish && criteria.explanation_en ? criteria.explanation_en : criteria.explanation;
    },

    downloadExcel() {
      // Prepare data for Excel
      const data = [];

      // Add header row
      const headerRow = [
        this.$t('results.criteria'),
        this.$t('results.explanation'),
        ...this.candidatesList.map(c => c.name)
      ];
      data.push(headerRow);

      // Add criteria rows (first 3)
      this.criterias.slice(0, 3).forEach(criteria => {
        const row = [
          this.getCriteriaText(criteria),
          this.getExplanationText(criteria).replace(/<br>/g, '\n').replace(/<[^>]*>/g, ''),
          ...this.candidatesList.map(candidate => {
            return this.evaluations[criteria.id] && this.evaluations[criteria.id][candidate.id]
              ? this.evaluations[criteria.id][candidate.id]
              : 0;
          })
        ];
        data.push(row);
      });

      // Add first total row
      const totalRow1 = [
        this.$t('results.total'),
        '',
        ...this.candidatesList.map(candidate => this.getTotalScoreForCandidateByRows(candidate.id, 1, 3))
      ];
      data.push(totalRow1);

      // Add criteria rows (4-5)
      this.criterias.slice(3, 5).forEach(criteria => {
        const row = [
          this.getCriteriaText(criteria),
          this.getExplanationText(criteria).replace(/<br>/g, '\n').replace(/<[^>]*>/g, ''),
          ...this.candidatesList.map(candidate => {
            return this.evaluations[criteria.id] && this.evaluations[criteria.id][candidate.id]
              ? this.evaluations[criteria.id][candidate.id]
              : 0;
          })
        ];
        data.push(row);
      });

      // Add second total row
      const totalRow2 = [
        this.$t('results.total'),
        '',
        ...this.candidatesList.map(candidate => this.getTotalScoreForCandidateByRows(candidate.id, 4, 5))
      ];
      data.push(totalRow2);

      // Add remaining criteria rows
      this.criterias.slice(5).forEach(criteria => {
        const row = [
          this.getCriteriaText(criteria),
          this.getExplanationText(criteria).replace(/<br>/g, '\n').replace(/<[^>]*>/g, ''),
          ...this.candidatesList.map(candidate => {
            return this.evaluations[criteria.id] && this.evaluations[criteria.id][candidate.id]
              ? this.evaluations[criteria.id][candidate.id]
              : 0;
          })
        ];
        data.push(row);
      });

      // Add grand total row
      const grandTotalRow = [
        this.$t('results.grandTotal'),
        '',
        ...this.candidatesList.map(candidate => this.getTotalScoreForCandidate(candidate.id))
      ];
      data.push(grandTotalRow);

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Results");

      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Criteria column
        { wch: 50 }, // Explanation column
        ...this.candidatesList.map(() => ({ wch: 15 })) // Candidate columns
      ];

      // Generate and download file
      XLSX.writeFile(wb, `Baobab_Challenge_2025_Results.xlsx`);
    },
  },
  mounted() {
    this.fetchEvaluationResults();
  },
  // Your script logic goes here
};
</script>

<style scoped>
/* Table container with horizontal scroll */
.table-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  margin-bottom: 20px;
}

/* Add your table styling here */
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* Minimum width to trigger scroll */
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
  white-space: nowrap; /* Prevent text wrapping in cells */
}

th {
  background-color: #f2f2f2;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Sticky first two columns (optional - for better UX) */
.sticky-col {
  position: sticky;
  left: 0;
  background-color: #f2f2f2;
  z-index: 11;
  min-width: 150px;
  max-width: 200px;
  white-space: normal; /* Allow wrapping in first column */
}

.sticky-col-2 {
  position: sticky;
  left: 150px; /* Adjust based on first column width */
  background-color: #f2f2f2;
  z-index: 11;
  min-width: 200px;
  max-width: 300px;
  white-space: normal; /* Allow wrapping in second column */
}

/* For tbody cells that should also be sticky */
tbody td.sticky-col {
  background-color: #fff;
  position: sticky;
  left: 0;
  z-index: 9;
}

tbody td.sticky-col-2 {
  background-color: #fff;
  position: sticky;
  left: 150px;
  z-index: 9;
}

/* Strong text in sticky columns */
tbody td.sticky-col strong,
tbody td.sticky-col-2 strong {
  font-weight: 600;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .table-container {
    border: 1px solid #ddd;
    border-radius: 5px;
  }
  
  th,
  td {
    font-size: 12px;
    padding: 6px;
  }

  .sticky-col {
    min-width: 100px;
    max-width: 150px;
  }

  .sticky-col-2 {
    left: 100px;
    min-width: 150px;
    max-width: 200px;
  }

  tbody td.sticky-col-2 {
    left: 100px;
  }
}

.winner-card {
  background-color: #f8f9fa;
  border-radius: 10px;
}

.winner-row {
  margin: 10px 0;
}

.v-card-title {
  color: #333;
  font-weight: bold;
}

.v-card-subtitle {
  padding: 10px;
}

.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 999;
}

.center-data {
  margin: 0 auto;
  align-content: center;
  justify-content: center;
}
</style>